import React from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';

import { PATH } from 'src/features/navigation/utils/constants';
import { pageTitles } from 'src/features/navigation/utils/messages';
import { useTitleStore } from 'src/stores/useTitleStore';
import { useParking } from 'src/providers/ParkingProvider';
import { useMediaQuery } from 'src/hooks/use-media-query';
import { toast } from 'src/registry/hooks/UseToast';
import {
    DropdownMenuContent,
    DropdownMenuSeparator,
    DropdownMenuLabel,
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuItem,
} from 'src/registry/ui/DropdownMenu';
import { Button } from 'src/registry/ui/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faParkingCircle, faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { type Parking, MEMBERSHIP_ROLES, type UserMembership } from 'src/features/parking/types';
import { messages } from 'src/features/parking/utils/messages';

interface HeaderProps extends React.HTMLAttributes<HTMLDivElement> {
    userMemberships: UserMembership[];
}

const Header = ({ userMemberships }: HeaderProps) => {
    const { formatMessage } = useIntl();
    const { parkingName, setParkingId, setParkingName, setParkingRole } = useParking();

    const [pageTitle, setPageTitle] = React.useState('');
    const storeTitle = useTitleStore((state) => state.title);

    const { pathname } = useLocation();
    const navigate = useNavigate();

    const isMobile = useMediaQuery('(max-width: 639px)');

    const iconSize = !isMobile ? 'smIcon' : 'mdIcon';

    const pathRoot = React.useMemo(() => {
        // We need to get the first part of the pathname
        return pathname.split('/')[1] as PATH;
    }, [pathname]);

    React.useEffect(() => {
        // Map the page paths to a page title
        const getPageTitle = (path: PATH) => {
            switch (path) {
                // case PATH.VEHICLES:
                //     return formatMessage(pageTitles.vehicles);
                default:
                    return formatMessage(pageTitles.smartPark);
            }
        };

        let pathName = '';
        if (pathRoot) {
            pathName = getPageTitle(pathRoot);
        }

        setPageTitle(pathName || formatMessage(pageTitles.home));
    }, [formatMessage, pathRoot]);

    const handleParkingChange = (parking: Parking, role: MEMBERSHIP_ROLES) => {
        setParkingId(parking.id);
        setParkingName(parking.name);
        setParkingRole(role);
        void navigate('/');
        toast({
            title: formatMessage(messages.currentParkingChangeSuccess),
            description: (
                <FormattedMessage
                    {...messages.currentParkingChangeSuccessDetails}
                    values={{
                        parkingName: parking.name,
                        b: (chunks: React.ReactNode[]) => <b className="font-semibold">{chunks}</b>,
                    }}
                />
            ),
        });
    };

    return (
        <header className="-my-2 flex items-center justify-between gap-4 overflow-hidden bg-white py-2 sm:sticky sm:top-0 sm:z-30 sm:-mx-8 sm:px-8 dark:bg-gray-900">
            <h1 className="relative text-3xl font-semibold tracking-tight sm:text-nowrap">
                <div className="flex items-center gap-4">{storeTitle || pageTitle}</div>
            </h1>
            {userMemberships?.length > 1 && (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button size={iconSize} variant="sm">
                            <FontAwesomeIcon icon={faParkingCircle} className="text-base" />
                            <span className={`hidden ${!isMobile ? 'lg:block' : 'md:block'}`}>{parkingName}</span>
                            <FontAwesomeIcon
                                icon={faChevronDown}
                                className={`hidden text-xs ${!isMobile ? 'lg:block' : 'md:block'}`}
                            />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="z-40" align="end">
                        <DropdownMenuLabel>{formatMessage(messages.parkings)}</DropdownMenuLabel>
                        <DropdownMenuSeparator />
                        <div className="max-h-60 overflow-y-auto">
                            {userMemberships?.map(({ parking, role }) => (
                                <DropdownMenuItem
                                    key={parking.id}
                                    onClick={() => {
                                        handleParkingChange(parking, role);
                                    }}
                                    className="flex flex-col items-start"
                                >
                                    <p>{parking.name}</p>
                                    <p className="text-xs text-gray-600">
                                        {formatMessage(messages.lastOpened)}
                                        {': '}
                                        {new Date(parking.updated_at).toLocaleString()}
                                    </p>
                                </DropdownMenuItem>
                            ))}
                        </div>
                    </DropdownMenuContent>
                </DropdownMenu>
            )}
        </header>
    );
};

export default Header;
